import { createSlice, createSelector } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrAllergySlice = createSlice({
    name: 'epcrAllergies',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const allergy of ensureArray(action.payload)) {
                if (!state.data[allergy.pcrid]) {
                    state.data[allergy.pcrid] = [allergy];
                } else {
                    let idx = state.data[allergy.pcrid].findIndex(a => allergy.id === a.id);
                    if (idx >= 0) {
                        state.data[allergy.pcrid].splice(idx, 1, allergy);
                    } else {
                        state.data[allergy.pcrid] = [...state.data[allergy.pcrid], allergy];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const allergy of ensureArray(action.payload)) {
                let idx = state.data[allergy.pcrid].findIndex(a => a.id === allergy.id);
                if (idx >= 0) {
                    state.data[allergy.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrAllergySlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrAllergySlice.name);
const fetchEpcrAllergies = pcrid => network => network.get('/api/v1/epcrallergies/' + encodeURIComponent(pcrid), epcrAllergySlice.actions.addOrUpdate);
const fetchEpcrAllergyDefs = (network) => network.get('/api/v1/epcrallergydefs', epcrAllergySlice.actions.setDefs);
export const addEpcrAllergy = (network, data) => network.put('/api/v1/epcrallergy', data, epcrAllergySlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
export const removeEpcrAllergy = (network, data) => network.delete('/api/v1/epcrallergy/' + encodeURIComponent(data.id), epcrAllergySlice.actions.remove, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
// #endregion

// #region selectors
const selectEpcrAllergyDefs = state => state.epcrAllergies.defs;
const selectEpcrAllergies = state => state.epcrAllergies.data;

const eagerSelector = createEagerSelector(epcrAllergySlice.name, { mode: 'mapToArray'});
const selectEpcrAllergyByID = eagerSelector(createSelector([selectEpcrAllergies, (allergies, pcrid) => pcrid], (allergies, pcrid) => allergies[pcrid] ?? []));
const selectEpcrAllergy = pcrid  => state => {
    return selectEpcrAllergyByID(state, pcrid);
}
// #endregion

// #region listeners
const listen = {
    epcrAllergyAdded: (allergy, { dispatch }) => dispatch(epcrAllergySlice.actions.addOrUpdate(allergy)),
    epcrAllergyRemoved: (allergy, { dispatch }) => dispatch(epcrAllergySlice.actions.remove(allergy))
}
// #endregion

export const [useEpcrAllergies, realizeEpcrAllergies] = dataHookFactory(selectEpcrAllergy, initMapFactory(fetchEpcrAllergies, listen, pcrid => pcrid?.toString()));
export const [useEpcrAllergyDefs, realizeEpcrAllergyDefs] = dataHookFactory(selectEpcrAllergyDefs, { run: fetchEpcrAllergyDefs });

const reducerDetails = { name: epcrAllergySlice.name, reducer: epcrAllergySlice.reducer };
export default reducerDetails;

